import React from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { PublicRoute, ProtectedRoute, AdminRoute } from './ProtectedRoute';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import Home from './Home';
import Person from './components/Person/Person';
import Settings from './components/Settings/Settings';
import Admin from './Admin';
import LoginSignup from './LoginSignup';
import VerifyClaim from './VerifyClaim';
import LandingPage from './LandingPage';
import TopHeader from './components/Common/TopHeader';
import { LanguageProvider } from './contexts/LanguageContext';

// Wrapper to provide TopHeader with navigation and auth
function AppWithHeader() {
	const navigate = useNavigate();
	const location = useLocation();
	const { currentUser, logout } = useAuth();
	const [isAdmin, setIsAdmin] = React.useState(false);

	// Check if user is admin
	React.useEffect(() => {
		const checkAdminStatus = async () => {
			if (!currentUser) {
				setIsAdmin(false);
				return;
			}

			try {
				const getPerson = httpsCallable(functions, 'getPerson');
				const result = await getPerson({ personId: currentUser.uid });
				const person = result.data.person;
				setIsAdmin(person.isAdmin || false);
			} catch (error) {
				console.error('Error checking admin status:', error);
				setIsAdmin(false);
			}
		};

		checkAdminStatus();
	}, [currentUser]);

	// If on /admin, show My Dashboard in menu instead of Admin Dashboard
	const onAdminPage = location.pathname === '/admin';

	const handleSignOut = async () => {
		try {
			await logout();
			navigate('/');
		} catch (error) {
			// Optionally show error
		}
	};
	const handleSettings = () => {
		if (currentUser) {
			navigate(`/settings/${currentUser.uid}`);
		}
	};
	const handleDashboard = () => {
		if (currentUser) {
			navigate(`/${currentUser.uid}`);
		}
	};
	const handleAdmin = () => {
		if (onAdminPage && currentUser) {
			navigate(`/${currentUser.uid}`); // My Dashboard
		} else {
			navigate('/admin');
		}
	};

	return (
		<>
			<TopHeader
				onSignOut={handleSignOut}
				onSettings={handleSettings}
				onDashboard={handleDashboard}
				onAdmin={handleAdmin}
				isAdmin={isAdmin}
				showAdmin={!!currentUser && isAdmin && !onAdminPage}
				showDashboard={!!currentUser && !onAdminPage}
				showAuthActions={!!currentUser}
				adminLabelKey={onAdminPage ? 'myDashboard' : 'adminDashboard'}
				currentUser={currentUser}
			/>
			<div style={{ paddingTop: '56px' }}>
				{/* Offset for fixed header, adjust as needed */}
				<Routes>
					<Route
						path='/'
						element={
							<PublicRoute>
								<LandingPage />
							</PublicRoute>
						}
					/>
					<Route
						path='/login'
						element={
							<PublicRoute>
								<LoginSignup />
							</PublicRoute>
						}
					/>
					<Route
						path='/signup'
						element={
							<PublicRoute>
								<LoginSignup />
							</PublicRoute>
						}
					/>
					<Route
						path='/verify-claim/:token'
						element={
							<PublicRoute>
								<VerifyClaim />
							</PublicRoute>
						}
					/>
					<Route path='/home' element={<Home />} />
					<Route
						path='/admin'
						element={
							<AdminRoute>
								<Admin />
							</AdminRoute>
						}
					/>
					<Route path='/settings/:personId' element={<Settings />} />
					<Route path='/:personId' element={<Person />} />
				</Routes>
			</div>
		</>
	);
}

function App() {
	return (
		<AuthProvider>
			<LanguageProvider>
				<Router>
					<AppWithHeader />
				</Router>
			</LanguageProvider>
		</AuthProvider>
	);
}

export default App;
